import { InputHTMLAttributes, forwardRef, useState, useId } from 'react';
import { LucideIcon, Eye, EyeOff } from 'lucide-react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: LucideIcon;
  showPasswordToggle?: boolean;
}

/**
 * Input Component - Componente de input reutilizável seguindo design TAIS
 *
 * @example
 * ```tsx
 * <Input
 *   label="E-mail"
 *   type="email"
 *   placeholder="seu@email.com"
 *   icon={Mail}
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, icon: Icon, showPasswordToggle, type: propType, className = '', id, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const generatedId = useId();
    const isPassword = propType === 'password';
    const type = isPassword && showPassword ? 'text' : propType;

    // Generate unique IDs for accessibility
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;

    return (
      <div className="flex flex-col gap-2">
        {label && (
          <label htmlFor={inputId} className="text-[17px] font-medium text-[#CCCCCC]">
            {label}
          </label>
        )}
        <div className="relative">
          {Icon && (
            <Icon className="absolute left-5 top-1/2 -translate-y-1/2 text-[#888888] w-5 h-5 pointer-events-none" />
          )}
          <input
            ref={ref}
            id={inputId}
            type={type}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? errorId : undefined}
            className={`
              w-full h-16 rounded-lg px-5 text-[17px]
              bg-[#1a1a1a] border border-[#333333]
              text-white placeholder:text-[#888888]
              transition-all duration-200
              focus:outline-none focus:border-[#F4CC04] focus:ring-2 focus:ring-[#F4CC04]/20
              disabled:opacity-50 disabled:cursor-not-allowed
              ${Icon ? 'pl-14' : 'pl-5'}
              ${showPasswordToggle ? 'pr-14' : 'pr-5'}
              ${error ? 'border-[#e53935] focus:border-[#e53935] focus:ring-[#e53935]/20' : ''}
              ${className}
            `}
            {...props}
          />
          {isPassword && showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-5 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#CCCCCC] transition-colors"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          )}
        </div>
        {error && (
          <span id={errorId} className="text-sm text-[#e53935]" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
